import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { DATE_OPTIONS, resolveDateRange } from '../../dashboard/utils/date-presets';
import { toPeriodQuery, zonedCustom, zonedDay, zonedNow } from '../../connect/finance/connect-period';

// One period for Order Reports, kept in the URL so it survives a refresh and
// a sidebar move. Replace, not push: changing the period is not a page the
// back button should walk through.
//
// The presets are the dashboard's (spec 2026-09-22 §7) — Today through Last
// 60 Days plus Custom Range — replacing the Connect picker's own six, so the
// restaurant meets one vocabulary across the app. connect-period.js keeps
// serving the admin's Settlement screen (connect-finance.js), which is the
// only other mount of ConnectPeriodPicker. One side effect of the move:
// Week to Date is the dashboard's own week, which starts Sunday (dayjs's
// plain `startOf('week')`), not the ISO Monday week connect-period.js's
// 'this_week'/'last_week' used.
//
// CAREFUL: two DATE_OPTIONS ids carry invisible characters (a zero-width
// non-joiner and a zero-width space — see date-presets.js). They are the ids
// resolveDateRange switches on, so they are always used as read from
// DATE_OPTIONS and never retyped. URLSearchParams percent-encodes them and
// decodes them back unchanged, so ?period= round-trips.
export const CUSTOM_RANGE = 'customRange';

// The statement is read weekly in practice and this is the dashboard's own
// nearest preset; Today would make the page look empty first thing in the
// morning, every morning.
export const DEFAULT_PERIOD_PRESET = 'last7Days';

const PERIOD_PARAM = 'period';
const FROM_PARAM = 'from';
const TO_PARAM = 'to';
const DEFAULT_PERIOD = { preset: DEFAULT_PERIOD_PRESET, custom: { from: null, to: null } };

const known = (preset) => DATE_OPTIONS.some((option) => option.value === preset);

// Reading never falls back to another PRESET. "Custom Range with no days
// yet" is a real state — it is exactly what the dropdown writes the moment a
// reader picks Custom Range, before the calendar has been opened — and
// answering it with `last7Days` would unselect the choice just made and take
// the range picker away again. The window that an incomplete range resolves
// to is decided in resolveFinanceRange below, which is the only place that
// needs an answer while the reader is still picking.
export const readFinancePeriod = (searchParams) => {
  const preset = searchParams?.get(PERIOD_PARAM);
  if (!preset || !known(preset)) return DEFAULT_PERIOD;
  if (preset !== CUSTOM_RANGE) return { preset, custom: { from: null, to: null } };

  const from = dayjs(searchParams.get(FROM_PARAM));
  const to = dayjs(searchParams.get(TO_PARAM));
  // Both days or neither: one end on its own cannot bound a window, and
  // keeping half of one would let the picker show a range nothing queries.
  if (!from.isValid() || !to.isValid()) return { preset: CUSTOM_RANGE, custom: { from: null, to: null } };
  return { preset: CUSTOM_RANGE, custom: { from, to } };
};

// A complete custom range writes `?period=customRange&from=…&to=…`; an
// incomplete one writes `?period=customRange` alone, which readFinancePeriod
// gives straight back. Any other preset drops both days, so a range left
// over from an earlier choice cannot follow the reader around.
export const writeFinancePeriod = (searchParams, { preset, custom } = DEFAULT_PERIOD) => {
  const next = new URLSearchParams(searchParams);
  next.set(PERIOD_PARAM, preset);
  if (preset === CUSTOM_RANGE && custom?.from && custom?.to) {
    next.set(FROM_PARAM, custom.from.format('YYYY-MM-DD'));
    next.set(TO_PARAM, custom.to.format('YYYY-MM-DD'));
  } else {
    next.delete(FROM_PARAM);
    next.delete(TO_PARAM);
  }
  return next;
};

// `zone` is the store's timezone id, and it decides where the day starts. The
// window used to be cut at the reader's midnight, so a report answered
// differently depending on where the owner was looking from, and an order
// taken near closing fell into the neighbouring week. resolveDateRange
// defaults to the browser's clock for its "now", so `zonedNow(zone)` is
// passed in here to re-read that "now" in the store's zone FIRST — a preset
// like 'today' has to know which calendar day it is at the store before it
// can compute anything, not just re-stamp the browser's day onto the store's
// clock afterwards. Passing an unknown zone keeps the browser's "now", which
// is the honest fallback while the store is still unknown.
//
// This is also the ONLY place a period falls back. A Custom Range the reader
// has not finished picking has no days to query, so the window — and only
// the window — becomes the default one; the period itself stays Custom Range
// (see readFinancePeriod), so the picker stays on screen. `resolveDateRange`
// answers null for 'customRange', which is what makes the `||` below do it.
export const resolveFinanceRange = (period, zone) => {
  if (period?.preset === CUSTOM_RANGE && period.custom?.from && period.custom?.to) {
    return zonedCustom(period.custom, zone);
  }
  const now = zonedNow(zone);
  const range = resolveDateRange(period?.preset, now) || resolveDateRange(DEFAULT_PERIOD_PRESET, now);
  return { from: zonedDay(range.startdate, zone), to: zonedDay(range.enddate, zone) };
};

const useFinancePeriod = (zone) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = readFinancePeriod(searchParams);
  const setPeriod = (next) => setSearchParams(writeFinancePeriod(searchParams, next), { replace: true });
  const query = toPeriodQuery(resolveFinanceRange(period, zone));
  return { period, setPeriod, query };
};

export default useFinancePeriod;
